import { FilePdfOutlined } from "@ant-design/icons";
import { Image } from "antd";
import type { CSSProperties } from "react";
import { useMemo } from "react";
import { companionIsSelfModerationRejected, type CompanionMessageRow } from "./CompanionRoomType";
import { resolveAppMediaUrl } from "../../utils/resolveAppMediaUrl";
import { CsMessageTextContent } from "../../components/customer-service-chat/CsMessageTextContent";
import { renderChatMentionText, type ChatMentionProduct } from "./chatMentionUtil";
import "./CompanionRoomStyle.css";

/** 微信式引用条样式（见 CompanionRoomStyle.css .crm-msg-quote） */
const MSG_QUOTE = {
  fontSize: 12,
  color: "#b2b2b2",
  railColor: "#d0d0d0",
  railWidth: 2,
  railGap: 8,
  width: 220,
  marginTop: 4,
} as const;

function displayFileName(m: CompanionMessageRow): string {
  const n = m.fileName?.trim();
  if (n) return n;
  if (m.messageType === "PDF") return "PDF文件";
  if (m.messageType === "IMAGE") return "图片";
  if (m.messageType === "STICKER") return "表情";
  return "";
}

function moderationHint(m: CompanionMessageRow): string | null {
  if (companionIsSelfModerationRejected(m)) return null;
  const s = m.moderationStatus?.trim();
  if (s === "REJECTED") return "审核未通过";
  return null;
}

export function quotePreviewText(m: CompanionMessageRow): string {
  if (Boolean(m.deleted)) return "引用内容已撤回";
  if (Boolean(m.shielded)) return "引用内容已屏蔽";
  if (m.messageType === "IMAGE") return "[图片]";
  if (m.messageType === "STICKER") return "[表情]";
  if (m.messageType === "PDF") return m.fileName?.trim() || "[PDF]";
  const t = (m.content || "").trim();
  return t || "消息";
}

/**
 * 微信式引用：挂在气泡外下方。
 * 自己消息：右对齐 + 右侧竖线；别人消息：左对齐 + 左侧竖线。
 */
export function CompanionReplyQuoteBlock(props: {
  name: string;
  preview: string;
  alignSelf?: boolean;
}): JSX.Element {
  const rail = `${MSG_QUOTE.railWidth}px solid ${MSG_QUOTE.railColor}`;
  const style: CSSProperties = {
    fontSize: MSG_QUOTE.fontSize,
    color: MSG_QUOTE.color,
    maxWidth: MSG_QUOTE.width,
    marginTop: MSG_QUOTE.marginTop,
    ...(props.alignSelf
      ? {
          borderRight: rail,
          paddingRight: MSG_QUOTE.railGap,
          borderLeft: 0,
          paddingLeft: 0,
        }
      : {
          borderLeft: rail,
          paddingLeft: MSG_QUOTE.railGap,
          borderRight: 0,
          paddingRight: 0,
        }),
  };
  return (
    <div
      className={`crm-msg-quote${props.alignSelf ? " crm-msg-quote--self" : ""}`}
      style={style}
    >
      <span className="crm-msg-quote-text">
        {props.name}: {props.preview}
      </span>
    </div>
  );
}

export function CompanionRoomMessageContent(props: {
  m: CompanionMessageRow;
  onOpenPdf?: (url: string, name: string) => void;
  mentionProducts?: ChatMentionProduct[];
  onMentionProductClick?: (product: ChatMentionProduct) => void;
}): JSX.Element {
  const { m, onOpenPdf, mentionProducts = [], onMentionProductClick } = props;
  const deleted = Boolean(m.deleted);
  const shielded = Boolean(m.shielded);
  const modHint = moderationHint(m);
  const strikeSelfRejected = companionIsSelfModerationRejected(m);
  const blockCls = strikeSelfRejected ? "crm-msg-block crm-msg-block--self-rejected" : "crm-msg-block";
  const mentionInline = useMemo(
    () =>
      mentionProducts.length > 0
        ? (text: string) =>
            renderChatMentionText(text, mentionProducts, {
              onProductClick: onMentionProductClick,
            })
        : undefined,
    [mentionProducts, onMentionProductClick]
  );

  if ((m.messageType === "IMAGE" || m.messageType === "STICKER") && m.fileUrl?.trim()) {
    const src = resolveAppMediaUrl(m.fileUrl) ?? m.fileUrl;
    return (
      <div className={blockCls}>
        {deleted ? (
          <span className="crm-msg-deleted">已撤回</span>
        ) : shielded ? (
          <span className="crm-msg-shielded">已屏蔽</span>
        ) : (
          <>
            <Image
              src={src}
              alt={displayFileName(m) || (m.messageType === "STICKER" ? "表情" : "图片")}
              className="crm-msg-image"
              preview={{ mask: false, destroyOnHidden: true }}
            />
            {modHint ? <div className="crm-msg-mod-hint">{modHint}</div> : null}
          </>
        )}
      </div>
    );
  }

  if (m.messageType === "PDF" && m.fileUrl?.trim()) {
    const name = displayFileName(m);
    return (
      <div className={blockCls}>
        {deleted ? (
          <span className="crm-msg-deleted">已撤回</span>
        ) : shielded ? (
          <span className="crm-msg-shielded">已屏蔽</span>
        ) : (
          <>
            <button
              type="button"
              className="crm-pdf-card"
              onClick={() => onOpenPdf?.(m.fileUrl ?? "", name || "PDF")}
            >
              <FilePdfOutlined className="crm-pdf-ic" />
              <span className="crm-pdf-name">{name || "PDF"}</span>
              <span className="crm-pdf-hint">在线查看</span>
            </button>
            {modHint ? <div className="crm-msg-mod-hint">{modHint}</div> : null}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={blockCls}>
      {deleted ? (
        <span className="crm-msg-deleted">已撤回</span>
      ) : shielded ? (
        <span className="crm-msg-shielded">已屏蔽</span>
      ) : (
        <>
          <CsMessageTextContent content={m.content ?? ""} renderInline={mentionInline} />
          {modHint ? <div className="crm-msg-mod-hint">{modHint}</div> : null}
        </>
      )}
    </div>
  );
}
